import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useModal } from "@root/client/modal";
import { UseCases } from "@planzone/core";

import { ResourceAvatar } from "@common/resourceAvatar";
import { TrashIcon } from "@common/trashIcon";
import { useAuthentication } from "@planzone/view-models";
import { Safeguard } from "@scenes/safeguard";
import { MarkDownContent } from "../../markDownContent";
import { Reply } from "../reply";
import { Props } from "./types";
import styles from "./styles.scss";

function Comment({ resources, comment, replies, onReply }: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const { pushView } = useModal();
  const isPending = comment.uuid.match(/^__pending__/);
  const submissionDate = new Date(comment.createdDate);

  return (
    <div
      className={classNames(styles.comment, {
        [styles.pending]: isPending,
        [styles.ownComment]:
          comment.author && comment.author.uuid === account.resource.uuid,
      })}
    >
      <div className={styles.avatar}>
        <ResourceAvatar resource={comment.author} />
      </div>
      <div className={styles.main}>
        <div className={styles.header}>
          <p className={styles.author}>
            {comment.author.name}{" "}
            {!!comment.author.profession ? (
              <span className={styles.role}>{comment.author.profession}</span>
            ) : (
              comment.author.role && (
                <span className={styles.role}>
                  {t(
                    `roles.${comment.author.role.name}`,
                    comment.author.role.name,
                  )}
                </span>
              )
            )}
          </p>
          <p className={styles.submissionDate}>
            {submissionDate.toLocaleDateString()} {submissionDate.getHours()}h
            {submissionDate.getMinutes()}
          </p>
        </div>
        <div className={styles.content}>
          {comment.content.trim().startsWith("<") ? (
            <div
              dangerouslySetInnerHTML={{
                __html: MarkDownContent.styleMentions(
                  comment.content,
                  (comment.recipients.length > 0
                    ? comment.recipients
                    : resources
                  )
                    .map(({ name }) => name)
                    .filter((name) => name !== comment.replyTo?.author.name),
                ),
              }}
            />
          ) : (
            <p>
              <MarkDownContent
                content={comment.content}
                replacements={{
                  [MarkDownContent.ExpressionTypes.TEXT]: (text) => (
                    <span>{text}</span>
                  ),
                  [MarkDownContent.ExpressionTypes.RESOURCE]: (text) => (
                    <span style={{ color: "#269FFF" }}>{text}</span>
                  ),
                }}
                resources={
                  comment.recipients.length > 0 ? comment.recipients : resources
                }
              />
            </p>
          )}
        </div>
        {replies.length > 0 && (
          <div className={styles.replies}>
            {replies.map((reply) => (
              <Reply key={reply.uuid} comment={reply} resources={resources} />
            ))}
          </div>
        )}
        {!isPending && (
          <button
            className={styles.replyButton}
            onClick={() => onReply(comment)}
          >
            {t("comment.reply")}
          </button>
        )}
      </div>
      <Safeguard useCases={[UseCases.removeComment]} context={{ comment }}>
        {({ canRemoveComment }) =>
          canRemoveComment ? (
            <div
              className={styles.iconBox}
              onClick={() =>
                pushView({
                  key: UseCases.removeComment,
                  data: comment,
                })
              }
            >
              <TrashIcon className={styles.icon} />
            </div>
          ) : (
            <div className={styles.iconBox}>
              <div className={styles.icon} />
            </div>
          )
        }
      </Safeguard>
    </div>
  );
}

export { Comment };
